import { useEffect } from 'react';

/** ID of the search page's field; the shortcut focuses it when it is already on screen. */
export const SEARCH_INPUT_ID = 'global-search-input';

/** Where typing `/` is text, not a shortcut: fields, editors, comboboxes and open dialogs. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return true;
  return target.closest('[role="combobox"], [role="textbox"], [role="dialog"]') !== null;
}

/**
 * The `/` shortcut of the app shell (spec 110 §6, S150-8): focuses the search page's field when that
 * page is open, otherwise opens the search overlay. It never fires while the member is typing
 * somewhere else.
 */
export function useSearchShortcut(openOverlay: () => void): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey || event.defaultPrevented || isTypingTarget(event.target)) return;
      event.preventDefault();
      const field = document.getElementById(SEARCH_INPUT_ID);
      if (field) field.focus();
      else openOverlay();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [openOverlay]);
}
